import React from "react";
import { Clock, Circle } from "react-feather";
import SectionCard from "./SectionCard";

// THE house style for a record's "who did what, when" history (2026-09-09),
// promoted out of the Technical module's own timeline so every module tells the
// same story the same way.
//
// Purely presentational: it renders whatever entries it is handed, in the order
// it is handed them. Each page owns a small builder that turns its own record
// into entries - which is why this needs no API of its own. Most of this
// project's records already carry CreatedBy/ApprovedBy/RejectedBy/... plus
// dates, so a history is DERIVED from columns that already exist rather than
// copied into an audit table. That also means the history is complete for
// records created long before anyone thought to log one.
//
// Entry shape:
//   { key, label, at, by, detail, tone, icon, hasTime }
//
// `hasTime` is not decoration. Several of this project's actor dates are SQL
// `date`, not `datetime`, so they arrive as midnight - printing a clock time on
// those invents precision that does not exist and makes unrelated steps look
// simultaneous. A builder must set hasTime:false for any date-only column.
// See project_technical_activity_timeline_date_bugs in project memory.
export const TONE_COLOR = {
  neutral: "#607D8B",
  info: "#0D6EFD",
  success: "#1E7E34",
  warning: "#FD7E14",
  danger: "#DC3545",
  muted: "#6C757D",
};

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "-";

export const formatWhen = (iso, hasTime) => {
  if (!iso) return "-";
  const day = formatDay(iso);
  if (!hasTime) return day;
  return `${day}, ${new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};

const HistoryTimeline = ({ entries = [], title = "History", isLoading = false, error = null, emptyLabel = "Nothing recorded yet." }) => {
  if (isLoading) {
    return (
      <SectionCard icon={Clock} title={title}>
        <p className="mb-0 text-muted">Loading history...</p>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard icon={Clock} title={title}>
        <div className="alert alert-danger mb-0 py-2 px-3">
          <small>Could not load the history ({error?.message || "server error"}).</small>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard icon={Clock} title={title}>
      {entries.length === 0 ? (
        <p className="text-muted mb-0">{emptyLabel}</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {entries.map((entry, index) => {
            const Icon = entry.icon || Circle;
            const color = TONE_COLOR[entry.tone] || TONE_COLOR.neutral;
            return (
              <li key={entry.key ?? `${entry.label}-${index}`} className="d-flex mb-3">
                {/* Dot + connector, so the sequence reads as one thread rather
                    than a stack of unrelated lines. */}
                <div className="me-3 d-flex flex-column align-items-center">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 26, height: 26, background: `${color}1F`, border: `1px solid ${color}59`, color }}
                  >
                    <Icon size={14} />
                  </span>
                  {index < entries.length - 1 && (
                    <span className="flex-grow-1 mt-1" style={{ width: 2, background: `${color}2E`, minHeight: 18 }} />
                  )}
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div style={{ fontSize: 13 }}>
                    <strong style={{ color }}>{entry.label}</strong>
                    {entry.by && <span className="text-muted"> by {entry.by}</span>}
                  </div>
                  <div className="text-muted" style={{ fontSize: 11.5 }}>{formatWhen(entry.at, entry.hasTime)}</div>
                  {entry.detail && <div className="mt-1" style={{ fontSize: 12.5 }}>{entry.detail}</div>}
                  {/* Only an edit-style entry carries both - the old wording is
                      struck through under the new one so nothing is lost. */}
                  {entry.newValue && <div className="mt-1" style={{ fontSize: 12.5 }}>{entry.newValue}</div>}
                  {entry.oldValue && (
                    <div className="mt-1 text-muted text-decoration-line-through" style={{ fontSize: 12.5 }}>{entry.oldValue}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
};

export default HistoryTimeline;
